import { useEffect, useRef, useState } from 'react';
import { ensureGoogleReady, initializeGoogleIdentity } from '@/lib/social-auth';

function GoogleMark({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Signal-style Google CTA. Visual pill sits underneath a transparent GIS button
 * so clicks hit Google’s iframe while the UI matches the reference.
 */
export default function GoogleAuthButton({
  clientId,
  intent = 'login',
  disabled = false,
  onToken,
  className = '',
}) {
  const gisRef = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!clientId || !gisRef.current) return undefined;
    let cancelled = false;

    const mount = async () => {
      try {
        await ensureGoogleReady(clientId);
        if (cancelled || !gisRef.current) return;
        initializeGoogleIdentity(
          gisRef.current,
          clientId,
          (token) => {
            if (!disabled) onTokenRef.current?.(token);
          },
          intent,
        );
        if (!cancelled) setReady(true);
      } catch {
        if (!cancelled) setReady(false);
      }
    };

    mount();
    const onResize = () => {
      if (!cancelled && gisRef.current && clientId) {
        initializeGoogleIdentity(
          gisRef.current,
          clientId,
          (token) => {
            if (!disabled) onTokenRef.current?.(token);
          },
          intent,
        );
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
    };
  }, [clientId, intent, disabled]);

  const label = intent === 'signup' ? 'Sign up with Google' : 'Log in with Google';

  return (
    <div
      className={`relative w-full min-h-[48px] ${disabled ? 'opacity-55 pointer-events-none' : ''} ${className}`.trim()}
      data-testid="social-google-button"
    >
      <div className="auth-login-google w-full pointer-events-none select-none" aria-hidden="true">
        <GoogleMark />
        <span>{label}</span>
      </div>
      <div
        ref={gisRef}
        className={`auth-google-gis-overlay absolute inset-0 flex items-center justify-center overflow-hidden rounded-full ${
          ready ? 'opacity-0' : 'opacity-0'
        }`}
        aria-label={label}
      />
    </div>
  );
}
